import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { PronunciationSearchDialog } from "@/components/search/PronunciationSearchDialog";
import { TopicSearchDialog } from "@/components/search/TopicSearchDialog";
import type { SearchMode } from "@/components/search/search-mode";

interface GlobalSearchContextValue {
  /** Which search is open, or `null` when none is. */
  mode: SearchMode | null;
  /**
   * Open one of the two searches. The sidebar's rows pass their own mode; ⌘K
   * and the header field open topics, which is what "search" means by default.
   */
  openSearch: (mode?: SearchMode) => void;
  closeSearch: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null);

/**
 * The two search dialogs, mounted once above the routes.
 *
 * There is one instance of each, not one dialog with a scope switch: "find a
 * topic" and "hear this word" are different questions — a list of things to
 * study versus a player with a clip rail — and a tab strip made the second one
 * invisible behind a control nobody opened.
 *
 * It lives here rather than inside a layout so the sidebar (rendered by
 * `PublicLayout` / `AppShell`), the header, and anything added later all reach
 * the same instances — a second mount would mean two dialogs.
 */
export function GlobalSearchProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<SearchMode | null>(null);

  const openSearch = useCallback((next: SearchMode = "topics") => setMode(next), []);
  const closeSearch = useCallback(() => setMode(null), []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      // ⌘K belongs to search everywhere in the app, including inside a text
      // field — the browser's own binding is "focus the address bar", which is
      // never what someone pressing it in a study app means.
      event.preventDefault();
      setMode("topics");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const value = useMemo(
    () => ({ mode, openSearch, closeSearch }),
    [mode, openSearch, closeSearch],
  );

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      <TopicSearchDialog
        open={mode === "topics"}
        onOpenChange={(open) => setMode(open ? "topics" : null)}
      />
      <PronunciationSearchDialog
        open={mode === "pronunciation"}
        onOpenChange={(open) => setMode(open ? "pronunciation" : null)}
      />
    </GlobalSearchContext.Provider>
  );
}

export function useGlobalSearch(): GlobalSearchContextValue {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error("useGlobalSearch must be used inside GlobalSearchProvider");
  }
  return context;
}
